import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { ProfileSurveyModel } from 'types'
import { toast } from 'react-hot-toast'

const initialSurvey: Omit<ProfileSurveyModel, 'id'> = {
  relativeDoingActivities: 'parent',
  hasPictureBook: false,
  hasComicBooks: false,
  hasYouthNovel: false,
  hasYouthMagazine: false,
  canSpeakAnotherLangage: false,
  levelInTheOtherLanguage: 0,
  howOftenProfileRead: 0,
  timeProfileSpentReading: 0,
}

export default function ProfileSurveyCreatePage() {
  const { profileId } = useParams<{ profileId: string }>()
  const navigate = useNavigate()

  const [survey, setSurvey] = useState(initialSurvey)
  const [isSaving, setIsSaving] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setSurvey((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setSurvey((prev) => ({
      ...prev,
      [name]: Number(value),
    }))
  }

  const handleSliderChange =
    (name: string) => (event: Event, value: number | number[]) => {
      setSurvey((prev) => ({ ...prev, [name]: value as number }))
    }

  const handleSubmit = () => {
    setIsSaving(true)
    axios
      .post(`${base_url}/profile/${profileId}/survey`, survey)
      .then(() => {
        toast.success('Questionnaire créé avec succès !')
        navigate(`/profile/${profileId}`)
      })
      .catch((error) => {
        console.error('Échec de la création du questionnaire:', error)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <PageAppBar pageName={`Créer un questionnaire pour le profil`}>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '700px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Nouveau questionnaire de profil</Typography>

        <FormControl fullWidth>
          <InputLabel id="relative-select-label">
            Qui fait les activités ?
          </InputLabel>
          <Select
            labelId="relative-select-label"
            name="relativeDoingActivities"
            value={survey.relativeDoingActivities}
            label="Qui fait les activités ?"
            onChange={(e) =>
              setSurvey((prev) => ({
                ...prev,
                relativeDoingActivities: e.target.value,
              }))
            }
          >
            <MenuItem value="parent">Parent</MenuItem>
            <MenuItem value="close_circle">Cercle proche</MenuItem>
            <MenuItem value="other">Autre</MenuItem>
          </Select>
        </FormControl>

        <FormGroup sx={{ alignSelf: 'flex-start' }}>
          <Typography variant="subtitle1">Supports disponibles :</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={survey.hasPictureBook}
                onChange={handleChange}
                name="hasPictureBook"
              />
            }
            label="Albums illustrés"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={survey.hasComicBooks}
                onChange={handleChange}
                name="hasComicBooks"
              />
            }
            label="Bandes dessinées"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={survey.hasYouthNovel}
                onChange={handleChange}
                name="hasYouthNovel"
              />
            }
            label="Romans jeunesse"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={survey.hasYouthMagazine}
                onChange={handleChange}
                name="hasYouthMagazine"
              />
            }
            label="Magazines jeunesse"
          />
        </FormGroup>

        <FormControlLabel
          control={
            <Checkbox
              checked={survey.canSpeakAnotherLangage}
              onChange={handleChange}
              name="canSpeakAnotherLangage"
            />
          }
          label="Parle une autre langue ?"
          sx={{ alignSelf: 'flex-start' }}
        />

        {survey.canSpeakAnotherLangage && (
          <Box sx={{ width: '100%' }}>
            <Typography gutterBottom>
              Niveau dans l'autre langue (0-5)
            </Typography>
            <Slider
              name="levelInTheOtherLanguage"
              value={survey.levelInTheOtherLanguage}
              onChange={handleSliderChange('levelInTheOtherLanguage')}
              step={1}
              marks
              min={0}
              max={5}
              valueLabelDisplay="auto"
            />
          </Box>
        )}

        <TextField
          required
          fullWidth
          type="number"
          name="howOftenProfileRead"
          label="Fréquence de lecture (jours/semaine)"
          value={survey.howOftenProfileRead}
          onChange={handleNumericChange}
          inputProps={{ min: 0, max: 7 }}
        />
        <TextField
          required
          fullWidth
          type="number"
          name="timeProfileSpentReading"
          label="Temps passé à lire (minutes/jour)"
          value={survey.timeProfileSpentReading}
          onChange={handleNumericChange}
          inputProps={{ min: 0 }}
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 2, minWidth: '150px' }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Créer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
